import {
  useTheme,
  Stack,
  Typography,
  Avatar,
  Box,
  Divider,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { resizeImage } from '../../../api/cloudinary';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import './TopScorersAnimation.css';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { Player } from '@par/shared/common/types';

type PropTypes = {
  cutoff?: number;
  onUsernameClick?: (profileId: string) => void;
  profileId: string;
  questionIndex: number;
  sortedPlayers: Player[];
  spectate?: boolean;
  tournamentTotalPlayers?: number;
};

export const TournamentSitAndGoLeaderboard = ({
  cutoff,
  onUsernameClick,
  profileId,
  questionIndex,
  sortedPlayers,
  spectate,
  tournamentTotalPlayers,
}: PropTypes) => {
  const theme = useTheme();
  const [currentPlacement, setCurrentPlacement] = useState<number>(0);
  const [previousPlacement, setPreviousPlacement] = useState<number>();
  // const [blink, setBlink] = useState(false);
  const [showCurrentStandings, setShowCurrentStandings] = useState(false);

  useEffect(() => {
    if (!sortedPlayers) return;
    if (spectate) return;
    setShowCurrentStandings(true);
    const found = sortedPlayers.find((p) => p.profileId === profileId);
    if (questionIndex > 0) {
      setPreviousPlacement(currentPlacement);
    }
    if (previousPlacement !== found.rank) {
      // setBlink(true);
      setCurrentPlacement(found.rank);
      // const timeout = setTimeout(() => setBlink(false), 1000);
    }
  }, [sortedPlayers]);

  const showCutoffLine = (index: number) => {
    const ties = sortedPlayers.filter((p) => p.rank === cutoff).length;
    const offset = ties > 1 ? ties - 1 : 0;
    if (index === cutoff - 1 + offset) return true;
    return false;
  };

  const nameColor = (player: Player, questionIndex?: number) => {
    if (questionIndex === undefined) return 'white';
    if (player.timerLimitReached) return theme.palette.error.main;
    if (player.currentScore !== null) return theme.palette.underPar.main;
    return 'white';
  };

  function renderRow(props: ListChildComponentProps) {
    const { index, style } = props;

    const p = sortedPlayers[index];

    if (!p) return <></>;

    return (
      <ListItem
        dense
        style={{
          ...style,
          borderBottom: showCutoffLine(index) ? `2px solid` : null,
          borderColor: showCutoffLine(index)
            ? theme.palette.success.main
            : null,
        }}
        key={index}
        disablePadding
        sx={{
          border: p.profileId === profileId ? '1px solid' : 'none',
          borderColor: p.profileId === profileId ? 'primary.dark' : 'none',
        }}
      >
        <ListItemButton
          onClick={() =>
            spectate ? onUsernameClick && onUsernameClick(p.profileId) : null
          }
          sx={{
            cursor: spectate ? 'pointer' : 'default',
            '&:hover': {
              bgcolor: !spectate ? 'transparent' : null,
            },
          }}
        >
          <ListItemText
            primary={p.rank + '.'}
            sx={{
              marginRight: 1,
            }}
            primaryTypographyProps={{
              style: {
                fontWeight: p.profileId === profileId ? 600 : 'none',
              },
            }}
          />
          <Avatar
            src={resizeImage(p.avatarPublicId, 50, 50)}
            variant="rounded"
            sx={{
              height: 20,
              width: 20,
            }}
          >
            {!p.avatarPublicId ? p.username?.charAt(0) : null}
          </Avatar>
          <ListItemText
            primary={p.username}
            sx={{
              marginLeft: 1,
              marginRight: 2,
              width: { sm: '150px' },
            }}
            primaryTypographyProps={{
              style: {
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                fontWeight: p.profileId === profileId ? 600 : 'none',
                color: nameColor(p, questionIndex),
              },
            }}
          />
          {p.currentScore !== undefined && p.currentScore !== null ? (
            <ListItemText
              primary={`${p.currentScore > 0 ? '+' : ''}${p.currentScore}`}
              sx={{ textAlign: 'right' }}
              primaryTypographyProps={{
                style: {
                  width: '40px',
                  textAlign: 'left',
                  color: nameColor(p, questionIndex),
                },
              }}
            />
          ) : null}
          <ListItemText
            primary={`${p.parScore > 0 ? '+' : ''}${p.parScore}`}
            sx={{ textAlign: 'right' }}
            primaryTypographyProps={{
              style: {
                fontWeight: 600,
              },
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  }

  return (
    <Stack spacing={2} height={'100%'} overflow={'hidden'} mt={2}>
      {!spectate ? (
        <>
          <Stack justifyContent={'center'}>
            <Typography
              textAlign={'center'}
              letterSpacing={5}
              sx={{ color: theme.palette.dailies.main }}
            >
              YOUR POSITION
            </Typography>
            <Stack
              direction={'row'}
              display={'flex'}
              justifyContent={'center'}
              mt={1}
            >
              <Box
                position={'relative'}
                display={'flex'}
                justifyContent={'center'}
              >
                <Box
                  display={'flex'}
                  position={'absolute'}
                  left={'-20%'}
                  top={'50%'}
                  sx={{
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  {previousPlacement !== undefined ? (
                    currentPlacement < previousPlacement ? (
                      <KeyboardDoubleArrowUpIcon
                        sx={{ alignSelf: 'center' }}
                        color="success"
                      />
                    ) : currentPlacement > previousPlacement ? (
                      <KeyboardDoubleArrowDownIcon
                        sx={{
                          alignSelf: 'center',
                          color: theme.palette.demon.light,
                        }}
                      />
                    ) : null
                  ) : null}
                </Box>
                <Stack direction={'row'} spacing={3}>
                  <Typography
                    variant="h4"
                    textAlign={'center'}
                    fontWeight={600}
                    // className={blink ? 'blink-text' : ''}
                  >
                    {currentPlacement || '--'}
                  </Typography>
                  <Typography
                    variant="h4"
                    textAlign={'center'}
                    fontWeight={600}
                  >
                    /
                  </Typography>
                  <Typography
                    variant="h4"
                    textAlign={'center'}
                    fontWeight={600}
                  >
                    {tournamentTotalPlayers}
                  </Typography>
                </Stack>
              </Box>
            </Stack>
            <Typography textAlign={'center'} mt={2} color="success.main">
              Cutoff: {cutoff} or better
            </Typography>
          </Stack>
          <Divider />
        </>
      ) : null}
      <Stack height={'80%'} pb={2}>
        <Typography textAlign={'center'} color="primary" fontWeight={600}>
          Current Standings
        </Typography>
        <AutoSizer>
          {({ height, width }) => (
            <FixedSizeList
              height={height}
              width={width}
              itemSize={36}
              itemCount={sortedPlayers?.length}
              overscanCount={5}
            >
              {renderRow}
            </FixedSizeList>
          )}
        </AutoSizer>
      </Stack>
      <Box
        sx={{
          width: '100%',
          backgroundColor: '#18132a',
        }}
      >
        <Typography
          textAlign={'center'}
          mt={2}
          sx={{ color: theme.palette.dailies.main }}
        >
          Players Answered
        </Typography>
        <Typography
          variant="h5"
          textAlign={'center'}
          fontWeight={600}
          mt={2}
          mb={4}
        >
          {tournamentTotalPlayers}
        </Typography>
      </Box>
    </Stack>
  );
};
